import React, { useCallback, useLayoutEffect, useMemo } from "react";
import { AHSeparator, Box, useLayoutContext } from "arkh";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { KPIListQuery } from "./__generated__/KPIListQuery.graphql";
import { KPIListInputItem } from "./KPIListInputItem";

import { KPIListSectionHeader } from "./KPIListSectionHeader";
import {
  Platform,
  SectionListData,
  SectionListRenderItem,
} from "react-native";
import { OutlinedHeaderButton } from "../RoutineOccasion/RoutineOccasionScreen/OutlinedHeaderButton";
import { Features, useHasFeatureInTier } from "../../useHasFeatureInTier";
import { KPIMainNavigationProps } from "./KPIMainNavigationProps";
import { HasPermission } from "../../OS/Login/usePermissions";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

export const KPIList = React.memo(KPIListC);
function KPIListC(
  props: {
    sequenceNumber: number;
    routineId: string;
    hasMoved: boolean;
    teamId?: string;
    lastFocusedSectionId: string | null;
    setLastFocusedSectionId: (sectionId: string | null) => void;
    sectionIds?: string[];
    isActive: boolean;
    fetchKey: string;
  } & KPIMainNavigationProps
) {
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();
  const data = useLazyLoadQuery<KPIListQuery>(
    graphql`
      query KPIListQuery($routineId: ID!, $sequenceNumber: Int!, $teamId: ID) {
        store {
          ...useHasFeatureInTier_Store
          ...usePermissions_store
        }
        routine(id: $routineId) {
          id
          title
          occasion(sequenceNumber: $sequenceNumber) {
            id
            sequenceNumber
            startAt

            id
            occasionSections(teamId: $teamId) {
              id
              ...KPIListInputItem_routineOccasionSection
              section {
                id
                assignedTeam {
                  ...KPIListSectionHeader_team
                  id
                }
              }
              taskExecutions {
                ... on TaskExecutionNumber {
                  id
                  __typename
                  task {
                    id
                    unit
                  }
                  sectionVersion {
                    title
                  }
                  ...KPIListInputItem_taskExecution
                  ...TaskExecutionNumberComponent_TaskExecutionNumber
                  ...KPIListSectionHeader_taskExecutions
                }
              }
            }
          }
        }
      }
    `,
    {
      routineId: props.routineId,
      sequenceNumber: props.sequenceNumber,
      teamId: props.teamId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const hasInsights = useHasFeatureInTier(
    Features.FOLLOWUP_AND_HISTORY,
    data.store
  );
  const openInsights = useCallback(() => {
    props.navigation.navigateDeprecated("KPIAnalytics", {
      id: props.route.params.routineId,
      title: props.route.params.routineTitle,
      selectedSectionIds: props.route.params.sectionIds ?? undefined,
      selectedTeamId: props.route.params.teamId,
    });
  }, [
    props.navigation,
    props.route.params.routineId,
    props.route.params.routineTitle,
    props.route.params.sectionIds,
    props.route.params.teamId,
  ]);
  const openAdmin = useCallback(() => {
    props.navigation.navigateDeprecated("KPIEdit", {
      screen: "KPIEditMain",
      params: {
        routineId: props.route.params.routineId,
        title: props.route.params.routineTitle,
      },
    });
  }, [
    props.navigation,
    props.route.params.routineId,
    props.route.params.routineTitle,
  ]);

  useLayoutEffect(() => {
    props.navigation.setOptions({
      headerTitleAlign: "center",
      headerTitle: data?.routine?.title ?? "",
      headerRight: () => (
        <Box style={{ flexDirection: "row" }}>
          <HasPermission permission="ROUTINE_ADMIN" store={data.store}>
            <OutlinedHeaderButton
              hitSlop={{ top: 20, bottom: 15, left: 10, right: 10 }}
              pressRetentionOffset={{
                top: 20,
                bottom: 15,
                left: 10,
                right: 10,
              }}
              title="Admin"
              onPress={openAdmin}
            />
          </HasPermission>

          {hasInsights ? (
            <Box spaceLeft="small">
              <OutlinedHeaderButton
                containerStyle={{
                  paddingLeft: layout.space.small,
                  paddingRight: Platform.OS === "web" ? layout.space.medium : 0,
                }}
                hitSlop={{ top: 20, bottom: 15, right: 10 }}
                pressRetentionOffset={{
                  top: 20,
                  bottom: 15,
                  right: 10,
                }}
                onPress={openInsights}
                icon="analytics"
              />
            </Box>
          ) : null}
        </Box>
      ),
    });
  }, [
    data?.routine?.title,
    data.store,
    hasInsights,
    layout.space.medium,
    layout.space.small,
    openAdmin,
    openInsights,
    props,
  ]);

  const sections = useMemo(() => {
    const s = data?.routine?.occasion?.occasionSections ?? [];

    if (props.sectionIds) {
      return s.filter((s) => props.sectionIds?.includes(s.section.id));
    }
    return s;
  }, [data?.routine?.occasion?.occasionSections, props.sectionIds]);

  type SectionTeam = {
    team: (typeof sections)[number]["section"]["assignedTeam"];
  };
  type NumberTaskExecution = Extract<
    (typeof sections)[number]["taskExecutions"][number],
    { __typename: "TaskExecutionNumber" }
  >;

  type DataItem = [NumberTaskExecution, (typeof sections)[number]];
  type Data = SectionListData<DataItem, SectionTeam>;

  const sectionListData = sections.reduce((teams, section) => {
    const taskExecution = section.taskExecutions[0];
    if (
      taskExecution === undefined ||
      taskExecution.__typename !== "TaskExecutionNumber"
    ) {
      return teams;
    }

    const dataItem: DataItem = [taskExecution, section];

    const sectionTeam = teams.findIndex(
      (team) => team.team?.id === section.section.assignedTeam?.id
    );

    if (sectionTeam > -1) {
      return teams.map((team) =>
        team.team?.id === section.section.assignedTeam?.id
          ? {
              ...team,
              data: [...team.data, dataItem] as const,
            }
          : team
      );
    }
    return [
      ...teams,
      {
        team: section.section.assignedTeam,
        data: [dataItem] as const,
      },
    ];
  }, [] as Data[]);

  const firstTaskEx = sections[0]?.taskExecutions[0] ?? undefined;
  const unit =
    firstTaskEx?.__typename === "TaskExecutionNumber"
      ? firstTaskEx?.task.unit
      : "CUSTOM";

  const renderItem: SectionListRenderItem<DataItem, SectionTeam> = useCallback(
    ({ item: [item, section], index: i, section: s }) => {
      if (!data.routine?.occasion) {
        return null;
      }
      if (item.__typename !== "TaskExecutionNumber") {
        return null;
      }

      return (
        <KPIListInputItem
          key={item.id}
          sequenceNumber={data.routine.occasion.sequenceNumber}
          routineId={data.routine.id}
          occasionSection={section}
          taskExecution={item}
          lastItem={s.data.length - 1 === i}
          tabIndex={66}
        />
      );
    },
    [data.routine?.id, data.routine?.occasion]
  );

  const renderSectionHeader = useCallback(
    ({ section }: { section: Data }) => {
      if (
        (section.data.length === 1 &&
          section.data[0][0].sectionVersion.title === null) ||
        !section.team
      ) {
        return null;
      }
      return (
        <KPIListSectionHeader
          key={section.team?.id}
          taskExecutions={section.data.map((item) => item[0])}
          team={section.team}
          unit={unit}
        />
      );
    },
    [unit]
  );

  return (
    <KeyboardAwareScrollView
      style={{ flex: 1 }}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{
        paddingBottom: layout.space.large + insets.bottom,
      }}
      contentInset={{ bottom: layout.space.large }}
      contentInsetAdjustmentBehavior="automatic"
      automaticallyAdjustKeyboardInsets
    >
      {sectionListData.map((section) => (
        <React.Fragment key={section.team?.id}>
          {renderSectionHeader({ section })}
          {section.data.map((item, index) =>
            renderItem({
              item,
              index,
              section,
              separators: {
                highlight: () => {},
                unhighlight: () => {},
                updateProps: () => {},
              },
            })
          )}
          <AHSeparator />
        </React.Fragment>
      ))}
    </KeyboardAwareScrollView>
  );
}
